import { useCallback, useEffect, useRef, useState } from 'react';

const DRIVER_SCRIPT_ID = 'driverjs-script';
const DRIVER_STYLE_ID = 'driverjs-style';
const DRIVER_SCRIPT_SRC = 'https://cdn.jsdelivr.net/npm/driver.js@1.3.6/dist/driver.js.iife.js';
const DRIVER_STYLE_HREF = 'https://cdn.jsdelivr.net/npm/driver.js@1.3.6/dist/driver.css';

let assetsPromise = null;
let activeDriver = null;
let globalStartToken = 0;

const injectDriverStyles = () => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(DRIVER_STYLE_ID)) {
    return;
  }

  const link = document.createElement('link');
  link.id = DRIVER_STYLE_ID;
  link.rel = 'stylesheet';
  link.href = DRIVER_STYLE_HREF;
  document.head.appendChild(link);
};

const injectDriverScript = () =>
  new Promise((resolve, reject) => {
    const existingScript = document.getElementById(DRIVER_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(true), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Driver.js')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = DRIVER_SCRIPT_ID;
    script.src = DRIVER_SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.referrerPolicy = 'no-referrer';
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Driver.js'));
    document.head.appendChild(script);
  });

const loadDriverAssets = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }

  if (window.driver?.js?.driver) {
    injectDriverStyles();
    return Promise.resolve(true);
  }

  if (!assetsPromise) {
    assetsPromise = (async () => {
      injectDriverStyles();
      await injectDriverScript();
      return true;
    })().catch((error) => {
      assetsPromise = null;
      document.getElementById(DRIVER_SCRIPT_ID)?.remove();
      throw error;
    });
  }

  return assetsPromise;
};

const getVisibleTourTargets = (group) => {
  if (typeof document === 'undefined' || !group) {
    return [];
  }

  return Array.from(document.querySelectorAll(`[data-tg-group="${group}"][data-tg-tour]`))
    .map((target, index) => {
      const content = String(target.getAttribute('data-tg-tour') || '').trim();
      const title = String(target.getAttribute('data-tg-title') || '').trim();
      const order = Number(target.getAttribute('data-tg-order')) || index + 1;
      const rect = target.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0 && target.getClientRects().length > 0;

      return { target, content, title, order, index, isVisible };
    })
    .filter((step) => step.content && step.isVisible)
    .sort((a, b) => a.order - b.order || a.index - b.index);
};

const buildDriverSteps = (group) => {
  const seenOrder = new Set();

  return getVisibleTourTargets(group).reduce((steps, item) => {
    if (seenOrder.has(item.order)) {
      return steps;
    }

    seenOrder.add(item.order);
    steps.push({
      element: item.target,
      popover: {
        title: item.title || undefined,
        description: item.content,
      },
    });
    return steps;
  }, []);
};

const closeDriver = (driverObj) => {
  if (!driverObj) {
    return;
  }

  try {
    if (typeof driverObj.destroy === 'function') {
      driverObj.destroy();
    }
  } catch (error) {
    console.warn('Failed to close Driver.js tour', error);
  }
};

const waitForTargets = async (group, minSteps = 1, timeoutMs = 3000) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const targets = getVisibleTourTargets(group);
    if (targets.length >= minSteps && targets.some((target) => target.order === 1)) {
      return true;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  return false;
};

const waitForNextPaint = async (frames = 2) => {
  if (typeof window === 'undefined') {
    return;
  }

  for (let i = 0; i < frames; i += 1) {
    await new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  }
};

const mapDriverOptions = (options) => ({
  showButtons: ['previous', 'next', 'close'],
  showProgress: options?.showStepProgress ?? true,
  allowClose: options?.exitOnClickOutside !== false && options?.exitOnEscape !== false,
  overlayClickBehavior: options?.exitOnClickOutside === false ? 'nextStep' : 'close',
  animate: true,
  popoverClass: options?.dialogClass || '',
});

export const useTourGuide = ({ group, enabled = true, options = {}, minSteps = 1 } = {}) => {
  const driverRef = useRef(null);
  const startTokenRef = useRef(0);
  const startingRef = useRef(false);
  const mountedRef = useRef(false);
  const [ready, setReady] = useState(false);

  const resetDriverRef = useCallback(() => {
    driverRef.current = null;
  }, []);

  useEffect(() => {
    let mounted = true;
    mountedRef.current = true;

    if (!enabled) {
      setReady(false);
      return () => {
        mounted = false;
        mountedRef.current = false;
        startTokenRef.current += 1;
        globalStartToken += 1;
        startingRef.current = false;
        resetDriverRef();
      };
    }

    loadDriverAssets()
      .then(() => {
        if (mounted) {
          setReady(true);
        }
      })
      .catch((error) => {
        console.error('Unable to load Driver.js assets', error);
        if (mounted) {
          setReady(false);
        }
      });

    return () => {
      mounted = false;
      mountedRef.current = false;
      startTokenRef.current += 1;
      globalStartToken += 1;
      startingRef.current = false;
      resetDriverRef();
    };
  }, [enabled, resetDriverRef]);

  const startTour = useCallback(async () => {
    if (!enabled || startingRef.current) {
      return false;
    }

    startingRef.current = true;
    const startToken = startTokenRef.current + 1;
    startTokenRef.current = startToken;
    const currentGlobalStartToken = globalStartToken + 1;
    globalStartToken = currentGlobalStartToken;

    try {
      closeDriver(activeDriver);
      resetDriverRef();

      await loadDriverAssets();
      await waitForNextPaint(2);

      if (
        !mountedRef.current ||
        startToken !== startTokenRef.current ||
        currentGlobalStartToken !== globalStartToken
      ) {
        return false;
      }

      const targetsReady = await waitForTargets(group, minSteps);
      if (!targetsReady) {
        return false;
      }

      const steps = buildDriverSteps(group);
      if (steps.length < minSteps) {
        return false;
      }

      const driverFactory = window.driver?.js?.driver;
      if (typeof driverFactory !== 'function') {
        return false;
      }

      const driverObj = driverFactory({
        ...mapDriverOptions(options),
        steps,
      });

      activeDriver = driverObj;
      driverRef.current = driverObj;
      await waitForNextPaint(1);

      if (
        !mountedRef.current ||
        startToken !== startTokenRef.current ||
        currentGlobalStartToken !== globalStartToken
      ) {
        return false;
      }

      driverObj.drive();
      return true;
    } finally {
      startingRef.current = false;
    }
  }, [enabled, group, minSteps, options, resetDriverRef]);

  return { ready, startTour };
};

